import { DropDownList } from "@progress/kendo-react-dropdowns";
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import {
	addSmsProductFromProfileApi,
	getProfileSmsProductsApi,
	getSearchProfileSmsProductsApi,
} from "api/product";
import { getUserSearchSpeciesApi, updateVesselSmsInfoApi } from "api/user";
import { t, use } from "i18next";
import { InternationalCountryCode, Product } from "interface/common";
import { IProduct } from "interface/product";
import { IUserProfileVessel, Vessel } from "interface/vessel";
import React, { FC, useEffect, useState } from "react";
import CustomButton from "ui/Button";
import CustomDropDownList from "ui/dropDown";
import { CustomRowRenderNoTextChange } from "ui/grid/CustomGrid";
import Info from "ui/Info";
import { COLOR, ICON_NAMES } from "utils/constant";
import { useWindowWidth } from "hooks/hooks";

export const ChangeRegistration: FC<{
	vessel: any[];
	products: IProduct[];
	deleteProduct: (id: number) => void;
	countryCodes?: InternationalCountryCode[];
	setSmsProducts: (products: IProduct[]) => void;
}> = ({ vessel, products, deleteProduct, countryCodes, setSmsProducts }) => {
	const windowWidth = useWindowWidth();
	const isMobileView = windowWidth < 768;

	const handleSmsProductsUpdate = () => {
		getProfileSmsProductsApi()
			.then((response) => {
				setSmsProducts(response.data);
			})
			.catch((error) => {
				console.log(error);
			});
	};
	return (
		<div
			className="user-profile-setting-form-block"
			style={{ display: "flex", justifyContent: "space-between", gap: "30px" }}
		>
			<div style={{ width: isMobileView ? "100%" : "50%" }}>
				<div> {t("PhoneNumber") + " (Bruk formatet: 47XXXXXXXX)"}</div>
				<div style={{ marginTop: "20px" }}>
					<VesselGrid
						data={vessel}
						countryCodes={countryCodes}
						isMobileView={isMobileView}
					/>
				</div>
			</div>
			<div style={{ width: isMobileView ? "100%" : "50%" }}>
				<div> {t("DefaultProduct")}</div>
				<div style={{ marginTop: "20px" }}>
					<DropDownListItems
						isMobileView={isMobileView}
						handleAddNewProduct={() => {
							handleSmsProductsUpdate();
						}}
					/>
				</div>
				<div>
					<h4>{"Valgte sms-produkter"}</h4>

					<div>
						<SmsBuyerProductGrid
							data={products}
							deleteProduct={deleteProduct}
							isMobileView={isMobileView}
						/>
					</div>
				</div>
			</div>
		</div>
	);
};

const VesselGrid: FC<{
	data: IUserProfileVessel[];
	countryCodes?: InternationalCountryCode[];
	isMobileView: boolean;
}> = ({ data, countryCodes = [], isMobileView }) => {
	const [phoneNumbers, setPhoneNumbers] = useState<{
		[key: string]: { prefix: string; number: string };
	}>({});

	const [phoneNumberErrors, setPhoneNumberErrors] = useState<{
		[key: string]: string;
	}>({});

	const inputRefs = React.useRef<{ [key: string]: HTMLInputElement }>({});
	const [showConfirmationModal, setShowConfirmationModal] = useState(false);
	const [showFailedModal, setShowFailedModal] = useState(false);

	const savingDataIdRef = React.useRef("");

	const extractCountryCode = (phoneNumber: string): string => {
		if (!phoneNumber) return "";
		const match = phoneNumber?.match(/^(\d{1,2})(.*)/);
		return match ? `+${match[1]}` : "";
	};

	const extractNumber = (phoneNumber: string): string => {
		if (!phoneNumber) return "";
		const match = phoneNumber?.match(/^(\d{1,2})(.*)/);
		//console.log("match", match);
		return match ? match[2] : "";
	};

	const removeCountryCodePlus = (phoneNumber: string): string => {
		return phoneNumber.replace(/^\+/, "");
	};

	useEffect(() => {
		const initialPhoneNumbers: {
			[key: string]: { prefix: string; number: string };
		} = {};

		data.forEach((item) => {
			initialPhoneNumbers[item.hullId] = {
				prefix: extractCountryCode(item.smsPhoneNumber) || "+47",
				number: extractNumber(item.smsPhoneNumber),
			};
		});

		setPhoneNumbers(initialPhoneNumbers);
	}, []);

	const handlePhoneNumberChange = (
		vesselId: string,
		data: { prefix: string; number: string },
	) => {
		const updatedNumbers = { ...phoneNumbers };

		updatedNumbers[vesselId] = {
			prefix: data.prefix,
			number: data.number,
		};
		setPhoneNumbers(updatedNumbers);
	};

	const isNumberChanged = (hullId: string, smsPhoneNumber: string) => {
		const _smsphoneNumber =
			removeCountryCodePlus(phoneNumbers[hullId].prefix) +
				phoneNumbers[hullId]?.number || "";
		return _smsphoneNumber !== smsPhoneNumber;
	};

	const isNumberValid = (phoneNumber: string) => {
		const isValidPhoneNumber = /^\d{8}$/.test(phoneNumber);
		return isValidPhoneNumber;
	};

	const buttonDisabled = (hullId: string, smsPhoneNumber: string) => {
		if (!smsPhoneNumber && isNumberValid(phoneNumbers[hullId]?.number))
			return false;

		if (!smsPhoneNumber || smsPhoneNumber.trim() === "") return true;
		if (phoneNumberErrors[hullId] !== undefined) return true;
		if (phoneNumbers[hullId]?.number === "") return true;

		if (!isNumberValid(phoneNumbers[hullId]?.number)) return true;

		if (!isNumberChanged(hullId, smsPhoneNumber)) return true;

		return false;
	};

	const confirmChangePhone = (hullId: string) => {
		savingDataIdRef.current = hullId;
		setShowConfirmationModal(true);
	};
	const savePhoneNumber = async () => {
		if (!savingDataIdRef.current) return;
		const hullId = savingDataIdRef.current;
		const data = {
			vesselId: hullId,
			prefix: removeCountryCodePlus(phoneNumbers[hullId].prefix),
			number: phoneNumbers[hullId].number,
		};
		updateVesselSmsInfoApi(data)
			.then((response) => {
				// console.log(response);
			})
			.catch((error) => {
				console.log(error);
				setShowFailedModal(true);
			})
			.finally(() => {
				savingDataIdRef.current = "";
				setShowConfirmationModal(false);
			});
	};

	const renderPhoneEditor = (item: IUserProfileVessel) => (
		<div
			style={{
				display: "flex",
				flexDirection: isMobileView ? "column" : "row",
				justifyContent: "space-between",
				alignItems: isMobileView ? "stretch" : "center",
				gap: "10px",
				width: "100%",
			}}
		>
			<div
				style={{
					display: "flex",
					flexDirection: isMobileView ? "column" : "row",
					gap: "10px",
					width: "100%",
				}}
			>
				<CustomDropDownList
					style={{ width: isMobileView ? "100%" : "100px" }}
					dataItemKey=""
					textField=""
					data={countryCodes.map((code) => code.internationalDialing)}
					value={phoneNumbers[item.hullId]?.prefix || "Choose"}
					onChange={(e) => {
						const formData = {
							prefix: e.value,
							number: phoneNumbers[item.hullId]?.number || "",
						};

						handlePhoneNumberChange(item.hullId, formData);
					}}
					defaultItem={"Choose"}
					defaultValue={"Choose"}
					popupSettings={{ width: "auto" }}
				/>
				<div style={{ width: "100%" }}>
					<input
						style={{ paddingLeft: "10px", width: "100%", minHeight: isMobileView ? "44px" : undefined }}
						type="tel"
						aria-hidden={false}
						ref={(el) =>
							(inputRefs.current[item.hullId] = el as HTMLInputElement)
						}
						value={phoneNumbers[item.hullId]?.number || ""}
						onChange={(value) => {
							const formData = {
								prefix: phoneNumbers[item.hullId].prefix,
								number: value.target.value,
							};
							handlePhoneNumberChange(item.hullId, formData);
							setTimeout(() => {
								inputRefs.current[item.hullId]?.focus();
							}, 0);
						}}
					/>
				</div>
			</div>
			<CustomButton
				label={t("Save")}
				btnStyle={{
					backgroundColor: "transparent",
					border: "1px solid #006980",
					height: isMobileView ? "44px" : "26px",
					width: isMobileView ? "100%" : undefined,
				}}
				disabled={buttonDisabled(item.hullId, item.smsPhoneNumber)}
				onClick={() => confirmChangePhone(item.hullId)}
				color="black"
			/>
		</div>
	);

	return (
		<div>
			{isMobileView ? (
				<div className="user-profile-setting-mobile-list">
					{data.length === 0 && (
						<div className="user-profile-setting-mobile-empty">{t("NoRows")}</div>
					)}
					{data.map((item, index) => (
						<div
							key={`${item.hullId || item.registrationNumber}-${index}`}
							className="user-profile-setting-mobile-card"
						>
							<div className="user-profile-setting-mobile-card-header user-profile-setting-mobile-card-header--simple">
								<div className="user-profile-setting-mobile-card-title">
									{item.description || "-"}
								</div>
								<div className="user-profile-setting-mobile-card-subtitle">
									{item.registrationNumber || "-"}
								</div>
							</div>
							<div className="user-profile-setting-mobile-card-body">
								<div className="user-profile-setting-mobile-card-field">
									<span className="user-profile-setting-mobile-card-label">
										{t("SMS")}
									</span>
									{renderPhoneEditor(item)}
								</div>
							</div>
						</div>
					))}
				</div>
			) : (
				<Grid
					style={{ overflowY: "hidden" }}
					data={data}
					className="hide-virtual-scroll"
					rows={{ data: (row) => CustomRowRenderNoTextChange(row) }}
				>
					<GridColumn
						field="registrationNumber"
						title={t("RegistrationNumber")}
					/>
					<GridColumn field="description" title={t("VesselName")} />
					<GridColumn
						field="smsPhoneNumber"
						title={t("SMS")}
						width={400}
						editable={true}
						cells={{
							data: (props) => <td>{renderPhoneEditor(props.dataItem)}</td>,
						}}
					/>
				</Grid>
			)}
			<Info
				title={showFailedModal ? t("ErrorTitle") : t("ChangeVesselSmsPhone")}
				description={
					showFailedModal
						? t("ErrorDescription")
						: t("ChangeVesselSmsPhoneConfirmation")
				}
				show={showConfirmationModal || showFailedModal}
				close={() => setShowConfirmationModal(false)}
				actions={
					showFailedModal
						? [
								{
									text: "Close",
									btn: () => setShowFailedModal(false),
									textColor: COLOR.StatusRed,
								},
							]
						: [
								{
									text: t("Cancel"),
									btn: () => setShowConfirmationModal(false),
									textColor: COLOR.StatusRed,
								},

								{
									text: t("Confirm"),
									btn: savePhoneNumber,
									textColor: COLOR.StatusGreen,
								},
							]
				}
			/>
		</div>
	);
};

const SmsBuyerProductGrid: FC<{
	data: any[];
	deleteProduct: (id: number) => void;
	isMobileView: boolean;
}> = ({ data, deleteProduct, isMobileView }) => {
	return (
		isMobileView ? (
			<div className="user-profile-setting-mobile-list">
				{data.length === 0 && (
					<div className="user-profile-setting-mobile-empty">{t("NoRows")}</div>
				)}
				{data.map((item, index) => (
					<div
						key={`${item.id || item.description}-${index}`}
						className="user-profile-setting-mobile-card"
					>
						<div className="user-profile-setting-mobile-card-header user-profile-setting-mobile-card-header--simple">
							<div className="user-profile-setting-mobile-card-title">
								{item.description || "-"}
							</div>
						</div>
						<div className="user-profile-setting-mobile-card-actions">
							<CustomButton
								label={t("Delete")}
								onClick={() => deleteProduct(item.id)}
								iconName={ICON_NAMES.Delete}
								btnStyle={{ width: "100%", minHeight: "44px" }}
								color="black"
							/>
						</div>
					</div>
				))}
			</div>
		) : (
			<Grid
				style={{ overflowY: "hidden" }}
				data={data}
				className="hide-virtual-scroll"
				rows={{ data: (row) => CustomRowRenderNoTextChange(row) }}
			>
				<GridColumn field="description" title={t("Description")} />
				<GridColumn
					title=""
					width="150px"
					cells={{
						data: (props) => (
							<td>
								<CustomButton
									label={t("")}
									onClick={() => deleteProduct(props.dataItem.id)}
									iconName={ICON_NAMES.Delete}
									btnStyle={{
										height: "26px",
									}}
									color="black"
								/>
							</td>
						),
					}}
				/>
			</Grid>
		)
	);
};

const DropDownListItems = ({
	handleAddNewProduct,
	isMobileView,
}: {
	handleAddNewProduct: () => void;
	isMobileView: boolean;
}) => {
	const [specie, setSpecie] = useState<{ code: string; displayName: string }>();
	const [product, setProduct] = useState<{ id: string; name: string }>();
	const [productList, setProductList] = useState<Product[]>([]);

	const addSmsProductFromProfile = () => {
		if (!product || !specie || !specie.code) return;

		addSmsProductFromProfileApi(specie?.code, product.id)
			.then((response) => {
				//console.log(response);
				handleAddNewProduct();
			})
			.catch((error) => {
				console.log(error);
			});
	};

	const handleGetProductList = (code?: string) => {
		if (!specie || specie.code === "") return;
		if (productList.length > 0) return;

		getSearchProfileSmsProductsApi("", code ? code : specie.code)
			.then((response) => {
				setProductList(response as any);
			})
			.catch((error) => {
				console.log(error);
			});
	};
	const handleSelectSpecie = (specie: any) => {
		setSpecie(specie);
		setProductList([]);
		handleGetProductList(specie.code);
	};
	useEffect(() => {
		handleGetProductList();
	}, [specie]);

	const handleProduct = async (text: string) => {
		return getSearchProfileSmsProductsApi(text, specie?.code || "");
	};
	return (
		<div style={{ display: "flex", gap: "20px", flexDirection: "column" }}>
			<CustomDropDownList
				className="user-profile-setting-select-field"
				style={{ width: "100%" }}
				data={[]}
				onSelect={handleSelectSpecie}
				label={t("Species")}
				value={{
					code: specie?.code || null,
					displayName: specie?.displayName || t("ChooseSpecies"),
				}}
				dataItemKey="code"
				textField="displayName"
				defaultItem={{ code: null, displayName: t("All") }}
				filterable={true}
				serverFilter={true}
				serverFilterApi={getUserSearchSpeciesApi}
				popupSettings={{ width: "auto" }}
			/>
			<CustomDropDownList
				className="user-profile-setting-select-field"
				style={{ width: "100%" }}
				data={productList}
				dataItemKey="id"
				textField="name"
				value={{
					id: product?.id || null,
					name: product?.name || t("ChoseProduct"),
				}}
				onChange={(e) => setProduct(e.target.value)}
				label={t("Product")}
				onOpen={() => handleGetProductList()}
				disabled={!specie}
				popupSettings={{ width: "auto" }}
			/>
			{/* <CustomDropDownList
				data={[]}
				onSelect={(product) => setProduct(product)}
				label={t("Product")}
				disabled={!specie}
				value={{
					id: product?.id || null,
					name: product?.name || t("ChooseProduct"),
				}}
				dataItemKey="id"
				textField="name"
				defaultItem={{ id: null, name: t("All") }}
				filterable={true}
				serverFilter={true}
				serverFilterApi={handleProduct}
				className="height-inherit"
				//popupSettings={{ width: 400 }}
			/> */}
			<div style={{ display: "flex" }}>
				<CustomButton
					label={t("AddToSmProduct")}
					bgColor={COLOR.Green}
					disabled={!product}
					color="white"
					onClick={addSmsProductFromProfile}
					btnStyle={isMobileView ? { width: "100%", minHeight: "44px" } : undefined}
				/>
			</div>
		</div>
	);
};
